/**
 * ops id（`slug:No`。例: "aircloset:8010"）のパースと整形。表示名にコロンは含まれないため
 * 最後のコロンで分割する。サービス跨ぎで No が重複しても id は一意になる。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business ops 依頼 id（slug:No）の生成・パース・表示用 No 抽出を提供する
 * @graph-connects none
 */
import { SERVICE_NAME_TO_SLUG, SERVICE_SLUG_TO_NAME } from './services.js';
/**
 * "aircloset:8010" → { slug, serviceName, opsNo }。最後のコロンで分割し、slug が既知でなければ null。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business ops id 文字列をサービス（slug / service_name）と No に分解する
 * @graph-connects none
 */
export function parseOpsId(id) {
    const sep = id.lastIndexOf(':');
    if (sep <= 0 || sep === id.length - 1)
        return null;
    const slug = id.slice(0, sep);
    const opsNo = id.slice(sep + 1);
    const serviceName = SERVICE_SLUG_TO_NAME[slug];
    if (serviceName === undefined)
        return null;
    return { slug, serviceName, opsNo };
}
/**
 * service_name（or slug）+ No → `slug:No`。service_name を渡すと slug に正規化する。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business service と No から安定 id（slug:No）を組み立てる
 * @graph-connects none
 */
export function formatOpsId(serviceNameOrSlug, opsNo) {
    const slug = SERVICE_NAME_TO_SLUG[serviceNameOrSlug] ?? serviceNameOrSlug;
    return `${slug}:${opsNo}`;
}
/**
 * id から表示用の No 部分だけを取り出す（`aircloset:8010` → `8010`）。サービスは別途表示する前提。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business id からサービス接頭辞を落とした表示用 No を返す
 * @graph-connects none
 */
export function opsDisplayNo(id) {
    const sep = id.lastIndexOf(':');
    return sep >= 0 ? id.slice(sep + 1) : id;
}
/**
 * Firestore 発番の下限（No 帯の partition floor）。導入時のシート最大 No（~8,500）より十分上の
 * 10,000 から採番することで、シート（= BQ ミラー）と Firestore が **同時に live** な移行期間でも
 * No 帯が重ならず一意性が保てる。固定 floor なので legacy 側の「動く max」を毎回 BQ に問い合わせる
 * （polling）必要がなく、書き込み側は Firestore の atomic counter だけを見ればよい。段階廃止が
 * 長引いて採番が 10,000 に近づく懸念が出たら 20,000 へ引き上げる（gap は許容 = 連番性より一意性）。
 * @graph-connects none
 */
export const OPS_FIRESTORE_NO_FLOOR = 10000;
/**
 * id の No 帯から出所（provenance）を判定する: `No >= OPS_FIRESTORE_NO_FLOOR` なら Firestore 発番、
 * それ未満はシート由来の legacy。No が数値でない（空 No の `r<row_number>` 等）場合も legacy 扱い。
 * partition floor により番号を見るだけで出所が分かるため、両ストアが live な期間の UNION 表示で
 * バッジ分けや重複排除に使える。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business ops id の No 帯から出所（Firestore 発番 / シート legacy）を判定する
 * @graph-connects none
 */
export function opsNoIsFirestoreNative(id) {
    const no = Number.parseInt(opsDisplayNo(id), 10);
    return Number.isFinite(no) && no >= OPS_FIRESTORE_NO_FLOOR;
}
//# sourceMappingURL=id.js.map